/**
 * The closed vocabularies nightly graph consolidation reads and writes
 * (#14573, ADR-0118).
 *
 * Every one backs a plain `String` column — never a Postgres enum
 * (ADR-0107, `check:no-new-db-enums`) — so the tuple here IS the constraint.
 * Nothing in the database refuses a misspelling; the `z.enum` beside each
 * tuple is where a write gets rejected.
 *
 * The lists are DIFFERENT AXES and are kept apart on purpose:
 *
 *   - {@link BELIEF_EVIDENCE_STANCES} — what an `Evidence` row says about the
 *     `Belief` it is attached to. Lives on the `BeliefEvidence` edge, never on
 *     either node (ADR-0113 applied to this edge, ADR-0118 § 1).
 *   - {@link BELIEF_EVIDENCE_LINK_ORIGINS} — which process attached the edge.
 *     The read gate ignores `CONSOLIDATION` edges when deciding who may read a
 *     Belief (ADR-0118 § 5, {@link READ_GRANTING_LINK_ORIGINS}), and the pin
 *     rule ignores them when deciding whether a person confirmed it (§ 3).
 *   - {@link GRAPH_CONSOLIDATION_RUN_STATUSES} — one nightly run's outcome.
 *   - {@link GRAPH_CONSOLIDATION_CHANGE_KINDS} — one ledgered mutation (§ 8).
 *   - {@link EXCERPT_VERIFICATION_STATUSES} — whether an `Evidence` row's
 *     `sourceExcerpt` was found in its source (#14630, ADR-0119 § 1).
 *
 * CLIENT-SAFE: no `server-only`, no Prisma, nothing but zod — the admin
 * ledger renders these values.
 */

import { z } from 'zod'

/**
 * Whether evidence supports or refutes the Belief it backs.
 *
 * `SUPPORTS` is deliberately the spelling `BeliefLink.valence` already uses
 * for the Belief → Priority edge, so one word means "supports" on both edges.
 */
export const BELIEF_EVIDENCE_STANCES = ['SUPPORTS', 'REFUTES'] as const

export const beliefEvidenceStanceSchema = z.enum(BELIEF_EVIDENCE_STANCES)

export type BeliefEvidenceStance = (typeof BELIEF_EVIDENCE_STANCES)[number]

/**
 * The `@default` of `BeliefEvidence.stance`. Every edge written before
 * #14573 was read as support, so the column ships without a backfill.
 * `tests/unit/lib/graph-consolidation/vocabulary.test.ts` pins that the
 * schema default and this constant agree.
 */
export const DEFAULT_BELIEF_EVIDENCE_STANCE: BeliefEvidenceStance = 'SUPPORTS'

/**
 * Which process attached a `BeliefEvidence` edge: the extraction that wrote
 * the Belief, a consolidation run linking across pulses, a person, or the
 * extraction of a comment on a graph node (#15190, ADR-0128 § 3).
 */
export const BELIEF_EVIDENCE_LINK_ORIGINS = [
  'EXTRACTION',
  'CONSOLIDATION',
  'USER',
  'COMMENT',
  'SYSTEM_ASSESSMENT',
] as const

export const beliefEvidenceLinkOriginSchema = z.enum(
  BELIEF_EVIDENCE_LINK_ORIGINS,
)

export type BeliefEvidenceLinkOrigin =
  (typeof BELIEF_EVIDENCE_LINK_ORIGINS)[number]

/**
 * The `@default` of `BeliefEvidence.linkOrigin`. Every edge written before
 * #14573 was written by extraction.
 */
export const DEFAULT_BELIEF_EVIDENCE_LINK_ORIGIN: BeliefEvidenceLinkOrigin =
  'EXTRACTION'

/**
 * The origin of every edge a comment's Evidence is attached by (#15190,
 * ADR-0128 § 3). Deliberately NOT in {@link READ_GRANTING_LINK_ORIGINS}: the
 * author could already reach the node when they commented, and if that reach
 * later ends the comment must not keep it alive. Pinned by
 * `tests/unit/lib/graph-consolidation/vocabulary.test.ts`.
 */
export const COMMENT_LINK_ORIGIN =
  'COMMENT' as const satisfies BeliefEvidenceLinkOrigin

/**
 * The origin of every edge from a system assessment to the evidence it read
 * (#15197). Deliberately NOT in {@link READ_GRANTING_LINK_ORIGINS}: citing a
 * source grants nobody anything — who may open it is ADR-0124's gate on the
 * source itself. Pinned by
 * `tests/unit/lib/graph-consolidation/vocabulary.test.ts`.
 */
export const SYSTEM_ASSESSMENT_LINK_ORIGIN =
  'SYSTEM_ASSESSMENT' as const satisfies BeliefEvidenceLinkOrigin

/**
 * The link origins whose edge may GRANT read access to a Belief (#14574,
 * ADR-0118 § 5). `beliefReadableBy`'s source arm traverses only these edges,
 * so a `CONSOLIDATION` or `COMMENT` edge can change a Belief's confidence but
 * never who may read it.
 *
 * An ALLOW-list on purpose, not a deny-list: a future origin added to
 * {@link BELIEF_EVIDENCE_LINK_ORIGINS} defaults to NOT granting read until
 * someone decides here that it should. It must keep containing
 * {@link DEFAULT_BELIEF_EVIDENCE_LINK_ORIGIN}, or every edge written before
 * #14573 stops granting the read it granted then.
 */
export const READ_GRANTING_LINK_ORIGINS = [
  'EXTRACTION',
  'USER',
] as const satisfies readonly BeliefEvidenceLinkOrigin[]

/**
 * A `GraphConsolidationRun`'s outcome. `HALTED` is the circuit breaker
 * (ADR-0118 § 9): the run planned, found the plan too large, and wrote
 * nothing to the graph. `FAILED` is an error.
 *
 * `MANUAL` (#16955) is not a night's run at all: the one run per org that
 * holds the merges facilitators make by hand in the meeting room
 * (`./manual-merge-run.ts`). No nightly read asks for it — every "last
 * completed pass" read names `COMPLETED`, and the merge phase's carried
 * declines read only ended nightly runs — so it never stands in for one.
 */
export const GRAPH_CONSOLIDATION_RUN_STATUSES = [
  'RUNNING',
  'COMPLETED',
  'HALTED',
  'FAILED',
  'MANUAL',
] as const

/**
 * What a ledger view calls a run (#16955). A night's run is named by its
 * date; the manual run's date is a placeholder no night is ever for
 * (`MANUAL_MERGE_RUN_ORG_LOCAL_DATE`), so showing it would read as a run of
 * the year zero. It is "Manual merges" instead.
 */
export function graphConsolidationRunLabel(run: {
  status: string
  orgLocalDate: string
}): string {
  return run.status === 'MANUAL' ? 'Manual merges' : run.orgLocalDate
}

export const graphConsolidationRunStatusSchema = z.enum(
  GRAPH_CONSOLIDATION_RUN_STATUSES,
)

export type GraphConsolidationRunStatus =
  (typeof GRAPH_CONSOLIDATION_RUN_STATUSES)[number]

/**
 * One `GraphConsolidationChange`'s kind (ADR-0118 § 8).
 *
 * `LINK_PROPOSED` and `CONTRADICTION_FLAGGED` write nothing to the graph —
 * they record a change the job declined to make (§§ 3, 5). `REVERT` is the
 * ledgered inverse of an earlier change, pointed at it by
 * `revertOfChangeId`, so the ledger stays append-only history.
 *
 * `EXCERPT_UNVERIFIED` (#14630, ADR-0119 § 1) records the nightly
 * `verify-excerpts` phase stamping an `Evidence` row `UNVERIFIED`, which
 * zeroes its weight in every Belief it backs. It names the Evidence and the
 * lowest-id Belief it backs (the ledger's `beliefId` is required).
 *
 * `CLAIM_EDGE_ADDED` (#14629, ADR-0119 § 3) records one `BeliefClaimEdge`
 * link discovery wrote — for every adjudicated `SAME_CLAIM` / `CONTRADICTS`
 * pair, whether or not its evidence links were allowed. Its `beliefId` is
 * the edge's `beliefAId`; `after.beliefBId` names the other side.
 *
 * `BELIEF_RETIRED` (#14631) records the nightly `hygiene` phase setting a
 * live Belief's `invalidatedAt` because every read-granting piece of
 * evidence behind it was dismissed. `before: { invalidatedAt: null }`,
 * `after: { invalidatedAt, reason }`. Revertible: the revert clears
 * `invalidatedAt` while it still holds `after.invalidatedAt`.
 *
 * `TASK_STATE_SET` (#15562, ADR-0126 § 1a) records the nightly `task-status`
 * phase setting a task's status from evidence. Its `beliefId` is the
 * system's `decision` Belief on the task ("Marked done by …") and its
 * `evidenceId` the evidence it acted on; `before: { taskId, state }`,
 * `after: { taskId, state, status, signal, evidenceIds, reason }`.
 * Revertible — the task checkbox's undo IS that revert: it puts `before`'s
 * state back while the task still holds `after`'s.
 *
 * `TASK_BLOCKER_LINKED` (#15562) records the same phase linking an Issue's
 * `blocker` Belief to a task with `BLOCKS`. Its `beliefId` is the blocker;
 * `after: { taskId, beliefLinkId }`. Revertible: the revert deletes that
 * link.
 *
 * `ISSUE_LINK_INFERRED` (#15968) records the nightly `link-issues` phase
 * (or its one-off backfill) linking an unlinked issue or topic to a
 * priority it is about. Its `beliefId` is the item; `after: { priorityId,
 * beliefLinkId, relation, confidence, reason, retypedFrom }` — the ledger
 * row IS the link's provenance, which `belief_links` has no column for.
 * Revertible: removing the link in the room is that revert, and a reverted
 * pair is never proposed again.
 *
 * `ISSUE_LINK_CONFIRMED` (#15968) records a person keeping the item through
 * a `discussed` or `resolved` disposition: the inferred link it names
 * (`after.inferredChangeId`) is confirmed, at the raised confidence. Carries
 * the `runId` of the change it confirms, as a `REVERT` does. Not revertible.
 *
 * `ISSUE_MERGED` (#15970) records the nightly `merge-issues` phase folding a
 * duplicate open issue or topic into another. Its `beliefId` is the ABSORBED
 * item, which it closes; `after.survivorId` names the item it now points at,
 * with the reason and every row the merge wrote or moved, so the merge can
 * be undone exactly. Revertible: the room's un-merge control is that revert
 * (`./issue-merge-ledger.ts`), and an un-merged pair is never merged again.
 */
export const GRAPH_CONSOLIDATION_CHANGE_KINDS = [
  'CONFIDENCE_CHANGED',
  'LINK_ADDED',
  'LINK_PROPOSED',
  'CONTRADICTION_FLAGGED',
  'REVERT',
  'EXCERPT_UNVERIFIED',
  'CLAIM_EDGE_ADDED',
  'BELIEF_RETIRED',
  'TASK_STATE_SET',
  'TASK_BLOCKER_LINKED',
  'ISSUE_LINK_INFERRED',
  'ISSUE_LINK_CONFIRMED',
  'ISSUE_MERGED',
] as const

export const graphConsolidationChangeKindSchema = z.enum(
  GRAPH_CONSOLIDATION_CHANGE_KINDS,
)

export type GraphConsolidationChangeKind =
  (typeof GRAPH_CONSOLIDATION_CHANGE_KINDS)[number]

/**
 * `Evidence.excerptVerification` (#14630, ADR-0119 § 1): whether the row's
 * `sourceExcerpt` was found in its source. NULL is "not checked yet".
 *
 *   - `VERIFIED` — located in the source (a pulse conversation's own user
 *     turns, or a note-taker quote already located at write time).
 *   - `UNVERIFIED` — the source exists and the quote is not in it. Weighs 0
 *     in `computeBeliefConfidence`; the row still exists and still shows.
 *   - `AUTHORED` — a person typed it (`MANUAL` evidence, a user-edited
 *     excerpt), so it is not a quote.
 *   - `SOURCE_GONE` — there is no source left to check it against. Keeps its
 *     weight: "could not check" is not "checked and absent".
 */
export const EXCERPT_VERIFICATION_STATUSES = [
  'VERIFIED',
  'UNVERIFIED',
  'AUTHORED',
  'SOURCE_GONE',
] as const

export const excerptVerificationStatusSchema = z.enum(
  EXCERPT_VERIFICATION_STATUSES,
)

export type ExcerptVerificationStatus =
  (typeof EXCERPT_VERIFICATION_STATUSES)[number]

/**
 * A `BeliefClaimEdge`'s verdict (#14629, ADR-0119 § 3): the two Beliefs make
 * the same claim, or contradicting ones. Link discovery's third verdict,
 * `UNRELATED`, writes no edge, so it is not a member.
 *
 * Only `SAME_CLAIM` groups Beliefs in `Priority.rating`
 * (`computeClaimGroupSizes` in `src/lib/priority-rating-core.ts`);
 * `CONTRADICTS` is stored so the contradiction is durable and never changes
 * a rating.
 */
export const BELIEF_CLAIM_EDGE_VERDICTS = ['SAME_CLAIM', 'CONTRADICTS'] as const

export const beliefClaimEdgeVerdictSchema = z.enum(BELIEF_CLAIM_EDGE_VERDICTS)

export type BeliefClaimEdgeVerdict = (typeof BELIEF_CLAIM_EDGE_VERDICTS)[number]

/** The verdict that groups two Beliefs into one claim. */
export const SAME_CLAIM_VERDICT: BeliefClaimEdgeVerdict = 'SAME_CLAIM'
